import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Users, Search, Eye, AlertTriangle } from 'lucide-react';

import UserDialog from '../dialogs/UserDialog';
import DeleteUserDialog from '../dialogs/DeleteUserDialog';
import EmailDialog from '../dialogs/EmailDialog';
import StatusChip from '../StatusChip';

const UsersTab = ({ 
  isLoading, 
  setIsLoading, 
  currentUser, 
  showNotification, 
  logAction,
  handleViewProperty, // función para ver propiedades (pasada desde StaffDashboard)
  handleViewReport    // función para ver reportes (pasada desde StaffDashboard)
}) => {
  // User specific states
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [usersPagination, setUsersPagination] = useState({ page: 0, rowsPerPage: 10, count: 0 });
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editUserData, setEditUserData] = useState({});
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailData, setEmailData] = useState({ subject: '', message: '' });
  const [userReports, setUserReports] = useState([]);
  const [userProperties, setUserProperties] = useState([]);

  // Animation variant
  const slideUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.5, 
        ease: "easeOut" 
      } 
    }
  };

  // Search users
  const fetchUsers = async () => {
    if (!userSearchTerm) return;
    
    setIsLoading(true);
    try {
      const response = await axios.get(`/api/admin/users/search?term=${userSearchTerm}&page=${usersPagination.page}&limit=${usersPagination.rowsPerPage}`);
      setUsers(response.data.users);
      setUsersPagination({
        ...usersPagination,
        count: response.data.total
      });
    } catch (error) {
      console.error('Error al buscar usuarios:', error);
      showNotification('Error al buscar usuarios', 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle user search
  const handleUserSearch = (e) => {
    e.preventDefault();
    if (userSearchTerm) {
      fetchUsers();
    }
  };
  
  // Change page or rows per page
  const handleUsersPaginationChange = (event, newPage) => {
    setUsersPagination({
      ...usersPagination,
      page: newPage
    });
    fetchUsers();
  };
  
  const handleUsersRowsPerPageChange = (event) => {
    setUsersPagination({
      ...usersPagination,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0
    });
    fetchUsers();
  };
  
  // View user details
  const handleViewUser = async (user) => {
    setSelectedUser(user);
    setEditUserData({ ...user });
    
    // Get user reports and properties
    try {
      const [reportsRes, propertiesRes] = await Promise.all([
        axios.get(`/api/admin/users/${user.id}/reports`),
        axios.get(`/api/admin/users/${user.id}/properties`)
      ]);
      
      setUserReports(reportsRes.data);
      setUserProperties(propertiesRes.data);
    } catch (error) {
      console.error('Error al obtener datos del usuario:', error);
      showNotification('Error al obtener datos del usuario', 'error');
    }
    
    setUserDialogOpen(true);
    
    // Log action
    logAction(
      'USUARIO_VISTO',
      `Se ha visto el perfil del usuario ${user.nombre} (${user.id})`,
      user.id,
      'user'
    );
  };
  
  // Save user changes
  const handleSaveUser = async () => {
    setIsLoading(true);
    try {
      await axios.put(`/api/admin/users/${selectedUser.id}`, editUserData);
      
      showNotification('Usuario actualizado correctamente', 'success');
      logAction(
        'USUARIO_ACTUALIZADO',
        `Se ha actualizado el usuario ${selectedUser.nombre} (${selectedUser.id})`,
        selectedUser.id,
        'user'
      );
      
      setUserDialogOpen(false);
      if (userSearchTerm) {
        fetchUsers();
      }
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      showNotification(`Error al actualizar usuario: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Delete user
  const handleDeleteUser = async () => {
    setIsLoading(true);
    try {
      await axios.delete(`/api/admin/users/${selectedUser.id}`);
      
      showNotification('Usuario eliminado correctamente', 'success');
      logAction(
        'USUARIO_ELIMINADO',
        `Se ha eliminado el usuario ${selectedUser.nombre} (${selectedUser.id})`,
        selectedUser.id,
        'user'
      );
      
      setDeleteUserDialogOpen(false);
      setUserDialogOpen(false);
      if (userSearchTerm) {
        fetchUsers();
      }
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      showNotification(`Error al eliminar usuario: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Send email
  const handleSendEmail = async () => {
    setIsLoading(true);
    try {
      await axios.post(`/api/admin/users/${selectedUser.id}/email`, emailData);
      
      showNotification('Email enviado correctamente', 'success');
      logAction(
        'EMAIL_ENVIADO',
        `Se ha enviado un email a ${selectedUser.nombre} (${selectedUser.id}): ${emailData.subject}`,
        selectedUser.id,
        'email'
      );
      
      setEmailDialogOpen(false);
    } catch (error) {
      console.error('Error al enviar email:', error);
      showNotification(`Error al enviar email: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <motion.div 
        className="bg-white rounded-xl shadow-lg overflow-hidden"
        variants={slideUp}
      >
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-indigo-600" />
            <h2 className="text-xl font-semibold text-gray-800">Gestión de Usuarios</h2>
          </div>
          
          <form onSubmit={handleUserSearch} className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-indigo-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por ID, Email o Teléfono"
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-indigo-50/50 border border-indigo-100 rounded-lg text-gray-700 
                        placeholder-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <motion.button
              type="submit"
              className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-lg shadow-md"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              Buscar
            </motion.button>
          </form>
        </div>

        {users.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-indigo-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Teléfono</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Tipo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider">Plan</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-indigo-800 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-indigo-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.nombre}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.telefono || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {user.tipo_usuario_id === 1 && 'Cliente'}
                      {user.tipo_usuario_id === 2 && 'Vendedor'}
                      {user.tipo_usuario_id === 3 && 'Constructora'}
                      {user.tipo_usuario_id === 4 && 'Inmobiliaria'}
                      {user.tipo_usuario_id === 5 && 'Staff'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusChip status={user.estado} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{user.plan || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <motion.button
                        onClick={() => handleViewUser(user)}
                        className="p-1.5 text-indigo-600 hover:bg-indigo-100 rounded-full transition-colors"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <Eye className="w-4 h-4" />
                      </motion.button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {userSearchTerm && users.length === 0 && (
          <div className="py-12 flex flex-col items-center justify-center">
            <AlertTriangle className="w-12 h-12 text-amber-400 mb-4" />
            <p className="text-lg font-medium text-gray-700 mb-1">No se encontraron usuarios</p>
            <p className="text-gray-500">Intenta con otro término de búsqueda</p>
          </div>
        )}

        {!userSearchTerm && (
          <div className="py-12 flex flex-col items-center justify-center">
            <Search className="w-12 h-12 text-indigo-300 mb-4" />
            <p className="text-lg font-medium text-gray-700 mb-1">Busca usuarios para ver resultados</p>
            <p className="text-gray-500">Ingresa un ID, email o teléfono para comenzar</p>
          </div>
        )}

        {users.length > 0 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Mostrando {usersPagination.page * usersPagination.rowsPerPage + 1} a{' '}
              {Math.min((usersPagination.page + 1) * usersPagination.rowsPerPage, usersPagination.count)} de{' '}
              {usersPagination.count} usuarios
            </div>
            
            <div className="flex gap-2">
              <select
                value={usersPagination.rowsPerPage}
                onChange={(e) => handleUsersRowsPerPageChange(e)}
                className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value={5}>5 / página</option>
                <option value={10}>10 / página</option>
                <option value={25}>25 / página</option>
                <option value={50}>50 / página</option>
              </select>
              
              <div className="flex">
                <button
                  onClick={() => handleUsersPaginationChange(null, usersPagination.page - 1)}
                  disabled={usersPagination.page === 0}
                  className={`px-3 py-1 border ${
                    usersPagination.page === 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-indigo-600 hover:bg-indigo-50'
                  } rounded-l-lg transition-colors`}
                >
                  Anterior
                </button>
                <button
                  onClick={() => handleUsersPaginationChange(null, usersPagination.page + 1)}
                  disabled={usersPagination.page >= Math.ceil(usersPagination.count / usersPagination.rowsPerPage) - 1}
                  className={`px-3 py-1 border border-l-0 ${
                    usersPagination.page >= Math.ceil(usersPagination.count / usersPagination.rowsPerPage) - 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-indigo-600 hover:bg-indigo-50'
                  } rounded-r-lg transition-colors`}
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
      
      {/* User Dialogs */}
      <UserDialog 
        userDialogOpen={userDialogOpen}
        setUserDialogOpen={setUserDialogOpen}
        selectedUser={selectedUser}
        editUserData={editUserData}
        setEditUserData={setEditUserData}
        handleSaveUser={handleSaveUser}
        setDeleteUserDialogOpen={setDeleteUserDialogOpen}
        setEmailDialogOpen={setEmailDialogOpen}
        userReports={userReports}
        userProperties={userProperties}
        handleViewReport={handleViewReport} // Usar la función pasada desde StaffDashboard
        handleViewProperty={handleViewProperty} // Usar la función pasada desde StaffDashboard
      />
      
      <DeleteUserDialog 
        deleteUserDialogOpen={deleteUserDialogOpen}
        setDeleteUserDialogOpen={setDeleteUserDialogOpen}
        selectedUser={selectedUser}
        handleDeleteUser={handleDeleteUser}
      />
      
      <EmailDialog 
        emailDialogOpen={emailDialogOpen}
        setEmailDialogOpen={setEmailDialogOpen}
        selectedUser={selectedUser}
        emailData={emailData}
        setEmailData={setEmailData}
        handleSendEmail={handleSendEmail}
      />
    </>
  );
};

export default UsersTab;